import { Fragment, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectNoneItem,
  SelectTrigger,
  SelectValue,
  clearable,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/format";
import Pagination from "@/components/Pagination";
import { usePagination } from "@/hooks/usePagination";
import type { GlAuditEntry } from "@/lib/gl";

const ENTITY_TYPES = ["all", "account", "journal", "period", "reconciliation"];

export default function GeneralLedgerAudit() {
  const [entries, setEntries] = useState<GlAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState("all");
  const [action, setAction] = useState("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const pg = usePagination(entries);

  useEffect(() => {
    pg.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries]);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ limit: "200" });
      if (entity && entity !== "all") qs.set("entity_type", entity);
      if (action.trim()) qs.set("action", action.trim());
      const d = await api.get<{ entries: GlAuditEntry[] }>(
        `/gl/audit?${qs.toString()}`,
      );
      setEntries(d.entries);
    } catch (err) {
      toast.error((err as Error).message || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity]);

  const toggle = (id: number) => {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <Card className="shadow-card">
        <CardHeader className="flex flex-row items-end justify-between gap-3 space-y-0 flex-wrap">
          <div>
            <CardTitle className="text-lg">Audit Trail</CardTitle>
            <CardDescription>
              Append-only log of all GL actions. Expand a row to see the JSON
              payload.
            </CardDescription>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <Label htmlFor="entity">Entity</Label>
              <Select value={entity} onValueChange={clearable(setEntity)}>
                <SelectTrigger id="entity" className="w-[180px]">
                  <SelectValue placeholder="All entities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectNoneItem />
                  {ENTITY_TYPES.map((e) => (
                    <SelectItem key={e} value={e}>
                      {e === "all" ? "All entities" : e}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="act">Action contains</Label>
              <Input
                id="act"
                value={action}
                onChange={(e) => setAction(e.target.value)}
                placeholder="e.g. posted"
                className="w-[180px]"
              />
            </div>
            <Button variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]" />
                <TableHead>When</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Actor</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                    No audit entries match.
                  </TableCell>
                </TableRow>
              ) : (
                pg.pageItems.map((e) => (
                  <Fragment key={e.id}>
                    <TableRow>
                      <TableCell>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => toggle(e.id)}
                        >
                          {expanded.has(e.id) ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell>{formatDateTime(e.created_at)}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {e.entity_type}#{e.entity_id}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {e.action.replace(/_/g, " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>{e.actor_label || "System"}</TableCell>
                    </TableRow>
                    {expanded.has(e.id) && (
                      <TableRow>
                        <TableCell />
                        <TableCell colSpan={4} className="bg-muted/30">
                          <pre className="text-xs font-mono whitespace-pre-wrap break-all">
                            {e.payload || "(no payload)"}
                          </pre>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))
              )}
            </TableBody>
          </Table>
          {!loading && entries.length > 0 ? (
            <Pagination
              page={pg.page}
              pageCount={pg.pageCount}
              total={pg.total}
              from={pg.from}
              to={pg.to}
              onPrev={pg.prev}
              onNext={pg.next}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
